import { AxiosError, RawAxiosRequestHeaders } from 'axios'
import { isEmpty, isNil, omit, PropertyName } from 'lodash'
import isBoolean from 'lodash/isBoolean'
import { action, computed, flow, makeObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'

import { convertToStringWithSeparator } from 'Shared/utils'
import { findCaseInsensitiveKey } from 'Shared/utils/object'
import { storage } from 'Shared/utils/storage'

import { DEFAULT_PAGE_SIZE, SPLITTED_API_ITEMS_TOTAL } from 'Core/constants/defaultValues'
import {
	ConfigFilter,
	ConfigFilterItem,
	CustomGenerator,
	FilterParams,
	FilterSuffixData,
	PaginationParams,
	SearchConfigType,
	SearchFilterParams,
	SortingParams,
	TableUrlParams
} from 'Core/interfaces'
import { getFilterValue, prepareGenericParams, setURLParams } from 'Core/utils/storeHelpers'

export class TableStore<T> {
	constructor(storagePageSizeField: string) {
		this.storagePageSizeField = storagePageSizeField
		this.pageSize = storage.get(this.storagePageSizeField)
			? Number(storage.get(this.storagePageSizeField))
			: DEFAULT_PAGE_SIZE

	}

	storagePageSizeField: string = ''

	@observable accessor error: AxiosError | null = null
	@observable accessor isLoading: boolean = false
	@observable accessor isCountLoading: boolean = false
	@observable accessor isLoadingFilterParamsFromUrl: boolean = true
	@observable accessor items: Array<T> = []
	@observable accessor selectedRowKeys: number[] = []
	@observable accessor totalCount: number = 0
	@observable accessor pageNumber: number = 1
	@observable accessor pageSize: number = 0
	@observable accessor sortingParams: SortingParams | null = null
	@observable accessor searchQuery: string = ''
	@observable accessor searchFilters: SearchFilterParams[] = []
	@observable accessor filters: { [key: string]: FilterParams } = {}
	@observable accessor mainSearchFilter: SearchFilterParams | null = null

	@action.bound
	setIsLoadingFilterParamsFromUrl = (value: boolean) => {
		this.isLoadingFilterParamsFromUrl = value
	}

	@action.bound
	clearSorting = (): void => {
		this.sortingParams = null
	}

	getSearchFilterByFieldNames = computedFn(
		(searchFilterNames: SearchFilterParams['apiSearchFieldName'][]) => {
			return (
				this.searchFilters.find((filter) =>
					searchFilterNames.includes(filter.apiSearchFieldName)
				) || null
			)
		}
	)

	@action.bound
	clearSearchFiltersByNames = (apiSearchFieldNames: string[]) => {
		if (
			this.mainSearchFilter &&
			apiSearchFieldNames.includes(this.mainSearchFilter.apiSearchFieldName)
		) {
			this.mainSearchFilter = null
		}

		this.searchFilters = this.searchFilters.filter(
			(filter) => !apiSearchFieldNames.includes(filter.apiSearchFieldName)
		)
	}

	@action.bound
	setSearchFilter({
		filterType,
		apiSearchFieldName,
		data,
		UrlParam,
		UrlParamLabel,
		label,
		suffixData,
		isMainSearch
	}: SearchFilterParams) {
		if (!data) {
			this.clearSearchFilters()
			return
		}

		const searchFilterParams = {
			filterType,
			apiSearchFieldName,
			data,
			label,
			UrlParam,
			UrlParamLabel,
			suffixData,
			isMainSearch
		}

		this.mainSearchFilter = searchFilterParams
		this.searchFilters.push(searchFilterParams)
	}

	@action.bound
	initFilter({
		UrlParam,
		apiFilterFieldName,
		data,
		filterType,
		emptyValue,
		isDisabled
	}: FilterParams) {
		if (this.filters[apiFilterFieldName]) {
			return
		}
		this.filters[apiFilterFieldName] = {
			UrlParam,
			apiFilterFieldName,
			data,
			filterType,
			emptyValue,
			isDisabled
		}
	}

	@action.bound
	setFilter = ({
		filterType,
		apiFilterFieldName,
		data,
		UrlParam,
		emptyValue,
		isSecondLoad,
		isDisabled
	}: FilterParams) => {
		this.pageNumber = 1

		this.resetSelectedRows()
		this.filters[apiFilterFieldName] = {
			UrlParam,
			apiFilterFieldName,
			data,
			filterType,
			emptyValue,
			isSecondLoad,
			isDisabled
		}
	}

	@action.bound
	disableFilter = ({
		apiFilterFieldName,
		isDisabled
	}: Pick<FilterParams, 'apiFilterFieldName' | 'isDisabled'>) => {
		if (this.filters[apiFilterFieldName]?.isDisabled === isDisabled) {
			return
		}
		this.filters[apiFilterFieldName] = {
			...this.filters[apiFilterFieldName],
			isDisabled
		}
	}

	@action
	clearFilters = () => {
		this.filters = {}
	}

	@action
	clearMobileSearchFilters = () => {
		this.searchFilters = []
	}

	@action
	clearSearchFilters = () => {
		this.searchFilters = []
		this.mainSearchFilter = null
	}

	@computed
	get isSortingApplied(): boolean {
		return Boolean(this.sortingParams?.order) && Boolean(this.sortingParams?.order)
	}

	@computed
	get filtersCount(): number {
		const searchFiltersCount = this.searchFilters.reduce((acc, val) => {
			if (!!val.data && !val.isMainSearch) {
				acc++
			}
			return acc
		}, 0)

		const filtersCount = Object.values(this.filters).reduce((acc, val) => {
			if (!!getFilterValue(val.data)) {
				acc++
			}
			return acc
		}, 0)
		return searchFiltersCount + filtersCount
	}

	@computed
	get isSearchApplied(): boolean {
		const isSearch = this.searchFilters.some((fil) => !!fil.data)
		return isSearch || !!this.searchQuery
	}

	@computed
	get isFiltersApplied(): boolean {
		return Object.values(this.filters).some((fil) => !!getFilterValue(fil.data))
	}

	@computed
	get isSearchOrFiltersApplied(): boolean {
		return this.isFiltersApplied || this.isSearchApplied
	}

	@computed
	get isMobileFiltersApplied(): boolean {
		return this.isFiltersApplied || this.isSortingApplied
	}

	@computed
	get getFormattedTotal() {
		return convertToStringWithSeparator(this.totalCount)
	}

	@computed
	get pagination() {
		return {
			current: this.pageNumber,
			pageSize: this.pageSize,
			total: this.totalCount
		}
	}

	getFilterDataByFieldName = computedFn(
		(fieldName: FilterParams['apiFilterFieldName'], defaultValue) => {
			const filter = this.filters[fieldName]
			if (!filter) {
				return null
			}

			if (!isNil(filter?.data)) {
				return filter.data
			}
			// need this for the datepicker
			return filter?.isSecondLoad ? null : defaultValue
		}
	)

	getFilterDisabledByFieldName = computedFn(
		(fieldName: FilterParams['apiFilterFieldName']): boolean => {
			const filter = this.filters[fieldName]
			return isBoolean(filter?.isDisabled) && filter.isDisabled
		}
	)

	getSorting = computedFn((): string | null => {
		if (!this.sortingParams) {
			return null
		}

		const { field, order } = this.sortingParams

		return `${field}, ${order}`
	})

	@computed
	get hasSelectedRows() {
		return this.selectedRowKeys.length > 0
	}

	@computed
	get selectedItemsCount() {
		return this.selectedRowKeys.length
	}

	@action.bound
	resetSelectedRows() {
		this.selectedRowKeys = []
	}

	@action.bound
	setSelectedRowKeys(selectedRowKeys: number[]) {
		this.selectedRowKeys = selectedRowKeys
	}

	@action.bound
	setSorting(sortingParams: SortingParams | null) {
		this.sortingParams = sortingParams
		this.resetSelectedRows()
	}

	@action.bound
	setSearchQuery(text: string) {
		this.resetSelectedRows()

		this.searchQuery = text
	}

	@action.bound
	resetTable = (filterParams?: FilterParams[]) => {
		this.setSearchQuery('')
		this.clearSearchFilters()
		this.setPagination({ pageNumber: 1 })
		this.resetSelectedRows()
		this.clearFilters()
		if (filterParams) {
			filterParams.forEach((filterParam) => this.setFilter(filterParam))
		}
	}

	@action.bound
	setPagination(paginationParams: PaginationParams) {
		const { pageNumber, pageSize } = paginationParams
		this.resetSelectedRows()

		if (!!pageNumber) {
			this.pageNumber = pageNumber
		}

		if (!!pageSize && pageSize !== this.pageSize) {
			storage.set(this.storagePageSizeField, pageSize.toString())
			this.pageSize = pageSize
			this.pageNumber = 1
		}
	}

	@action.bound
	protected initSearchParamsFromUrl<Field, FormatEntity>(
		params: Record<string, string[]>,
		searchFilterKeys: string[],
		config: SearchConfigType<Field, FormatEntity>
	) {
		const activeSearchFilterKeys = searchFilterKeys.filter((UrlParam) =>
			findCaseInsensitiveKey(params, UrlParam)
		)

		if (activeSearchFilterKeys.length > 0) {
			activeSearchFilterKeys.forEach((filterKey) => {
				const value = params[filterKey][0]
				const filterConfig = Object.values(config).find(
					(filter) => filter.UrlParam === filterKey
				)
				let label = value
				if (filterConfig?.UrlParamLabel) {
					label = params[filterConfig.UrlParamLabel]?.[0] || label
				}

				let suffixData: FilterSuffixData | undefined
				if (filterConfig?.suffixData?.label) {
					suffixData = {
						urlParam: filterConfig.suffixData.urlParam,
						label: params[filterConfig.suffixData.urlParam]?.[0]
					}
				}

				if (filterConfig) {
					this.setSearchFilter({
						filterType: filterConfig.filterType,
						apiSearchFieldName: filterConfig.apiSearchFieldName,
						data: value,
						label: label,
						UrlParam: filterConfig.UrlParam,
						UrlParamLabel: filterConfig.UrlParamLabel,
						suffixData,
						isMainSearch: filterConfig.isMainSearch
					})
				}
			})
		}
	}

	@action.bound
	protected initFilterParamsFromUrl<T, K>(
		params: Record<string, string[]>,
		filterKeys: string[],
		filtersConfig: ConfigFilter<T, K>
	) {
		const activeFilterKeys = filterKeys.filter((UrlParam) =>
			findCaseInsensitiveKey(params, UrlParam)
		)

		if (activeFilterKeys.length > 0) {
			activeFilterKeys.forEach((filterKey) => {
				const value = params[filterKey]
				const updatedConfig = Object.values(filtersConfig) as ConfigFilterItem<string, K>[]
				const filterConfig = updatedConfig.find((filter) => filter.UrlParam === filterKey)
				if (filterConfig) {
					this.setFilter({
						filterType: filterConfig.filterType,
						apiFilterFieldName: filterConfig.apiFilterFieldName,
						data: value,
						UrlParam: filterConfig.UrlParam,
						emptyValue: filterConfig.emptyValue
					})
				}
			})
		}
	}

	@flow.bound
	*requestData<V extends TableUrlParams>({
		repository,
		defaultSort,
		fixQueryParam,
		isNotRequestingData,
		additionalParams,
		omitParams,
		withoutURLParams = false,
		isToCallCount = false,
		isSplittedAPI = false,
		headers
	}: {
		repository: (
			params: TableUrlParams,
			additionalParams?: V,
			headers?: RawAxiosRequestHeaders
		) => Promise<ListData<T>>
		defaultSort?: SortingParams
		fixQueryParam?: boolean
		isNotRequestingData?: boolean
		additionalParams?: V
		omitParams?: PropertyName[]
		withoutURLParams?: boolean
		isToCallCount?: boolean
		isSplittedAPI?: boolean
		headers?: RawAxiosRequestHeaders
	}): CustomGenerator<ListData<T>> {
		this.isLoading = !isToCallCount

		this.isCountLoading = isToCallCount

		!withoutURLParams &&
			setURLParams({ searchFilters: this.searchFilters, filters: this.filters })

		defaultSort && this.setSorting(defaultSort)

		try {
			const params = prepareGenericParams(this, fixQueryParam)
			const formattedParams = isNotRequestingData
				? omit({ ...params, ...additionalParams }, [
						'page',
						'pageSize',
						'sortField',
						'sortOrder',
						...(omitParams ? omitParams : [])
				  ])
				: params

			const omittedParams = omitParams ? omit(params, omitParams) : params

			const data =
				isEmpty(formattedParams) && isNotRequestingData
					? null
					: yield repository(omittedParams, additionalParams, headers)
			if (isSplittedAPI) {
				if (data?.total === 0) {
					this.items = []
					this.totalCount = 0
				} else {
					this.items =
						data?.total === SPLITTED_API_ITEMS_TOTAL ? data?.items || [] : this.items
					this.totalCount =
						data?.total === SPLITTED_API_ITEMS_TOTAL
							? this.totalCount
							: data?.total || 0
				}
				return
			}
			this.items = data?.items || []
			this.totalCount = data?.total || 0
		} catch (error) {
			throw error
		} finally {
			this.isLoading = false
			this.isCountLoading = false
		}
	}
}
